import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAdventContests } from "../../features/competition/competitionSlice";
import { useDispatch, useSelector } from "react-redux";
import AdventLeaderboard from "./AdventLeaderboard";
import Helmet from "react-helmet";

const ApexAfterDark = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [particles, setParticles] = useState([]);
  const [showLoginTooltip, setShowLoginTooltip] = useState(null);
  const [activeTab, setActiveTab] = useState("challenges"); // New state for tab switching

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAdventContests("apex-week-of-horror"));
  }, [dispatch]);

  const { adventContests, showSpinner, generalError } = useSelector(
    (state) => state.competitions
  );

  const { user } = useSelector((state) => state.auth || {});
  const isLoggedIn = user.length != 0;

  const challenges = adventContests?.[0]?.challenges || [];
  let filledChallenges = [...challenges];

  if (challenges.length < 8) {
    const missing = 8 - challenges.length;

    const challengeDates = [
      { date: "October 24th", emoji: "🎃" },
      { date: "October 25th", emoji: "🦇" },
      { date: "October 26th", emoji: "👻" },
      { date: "October 27th", emoji: "🕷️" },
      { date: "October 28th", emoji: "💀" },
      { date: "October 29th", emoji: "🧛" },
      { date: "October 30th", emoji: "🧙" },
      { date: "October 31st", emoji: "🎭" },
    ];

    const placeholders = Array.from({ length: missing }, (_, i) => {
      const challengeIndex = challenges.length + i;
      const dateInfo = challengeDates[challengeIndex] || {
        date: "Soon",
        emoji: "🔮",
      };

      return {
        id: `placeholder-${i}`,
        order: challengeIndex + 1,
        hide: true,
        name: "???",
        description: "This challenge hasn't been unlocked yet...",
        unlockDate: dateInfo.date,
        unlockEmoji: dateInfo.emoji,
      };
    });

    filledChallenges = [...challenges, ...placeholders];
  }

  // Handle particle effects on card hover
  const handleCardHover = (cardId, isPlaceholder) => {
    if (isPlaceholder) return; // No particles for placeholder cards

    const emojis = ["👻", "💀", "🕷️"];
    const newParticles = [];

    for (let i = 0; i < 3; i++) {
      newParticles.push({
        id: Date.now() + i,
        emoji: emojis[Math.floor(Math.random() * emojis.length)],
        left: Math.random() * 100,
        cardId: cardId,
      });
    }

    setParticles((prev) => [...prev, ...newParticles]);

    // Remove particles after animation
    setTimeout(() => {
      setParticles((prev) =>
        prev.filter((p) => !newParticles.find((np) => np.id === p.id))
      );
    }, 2000);
  };

  const handleCardClick = (e, challenge) => {
    if (!isLoggedIn || challenge.hide) {
      e.preventDefault();
    }
  };

  return (
    <>
      <Helmet>
        <title>Apex Week of Horror | Camp Apex</title>
        <meta
          name="description"
          content="Survive the Apex Week of Horror! Take on spooky Halloween-themed Salesforce Apex challenges. A week of frightfully fun coding problems to test your skills."
        />
        <meta property="og:title" content="Apex Week of Horror | Camp Apex" />
        <meta
          property="og:description"
          content="Halloween-themed Salesforce Apex challenges await. Can you survive the Apex Week of Horror? Daily coding challenges with a spooky twist."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`https://campapex.org${window.location.pathname}`}
        />
        <meta
          property="og:image"
          content="https://campapex.org/assets/hero6.png"
        />
        <link
          rel="canonical"
          href={`https://campapex.org${window.location.pathname}`}
        />
      </Helmet>
      <style jsx="true">{`
        @import url("https://fonts.googleapis.com/css2?family=Creepster&family=Nosifer&family=Griffy&family=Fira+Code:wght@400;600&family=Eater&display=swap");

        :root {
          --blood-orange: #ff4500;
          --midnight-purple: #2d1b69;
          --toxic-green: #39ff14;
          --bone-white: #f8f8e8;
          --shadow-black: #0a0a0a;
          --rust: #b7410e;
          --moonlight: #c0c5ce;
          --fog: rgba(148, 130, 186, 0.15);
        }

        .apex-after-dark {
          font-family: "Griffy", serif;
          background: linear-gradient(
            180deg,
            #0a0a0a 0%,
            #2d1b69 50%,
            #0a0a0a 100%
          );
          color: var(--bone-white);
          min-height: 100vh;
          overflow-x: hidden;
          position: relative;
        }

        .apex-after-dark::before {
          content: "";
          position: fixed;
          top: 0;
          left: -100%;
          width: 300%;
          height: 100%;
          background: radial-gradient(
            ellipse at center,
            transparent 0%,
            var(--fog) 40%,
            transparent 70%
          );
          animation: fogDrift 30s ease-in-out infinite;
          pointer-events: none;
          z-index: 1;
        }

        @keyframes fogDrift {
          0%,
          100% {
            transform: translateX(0) rotate(0deg);
          }
          50% {
            transform: translateX(30%) rotate(180deg);
          }
        }

        .bat {
          position: fixed;
          font-size: 2rem;
          animation: fly 15s infinite linear;
          z-index: 2;
          pointer-events: none;
          filter: drop-shadow(0 0 10px var(--midnight-purple));
        }

        .bat:nth-child(1) {
          top: 10%;
          animation-delay: 0s;
          animation-duration: 18s;
        }
        .bat:nth-child(2) {
          top: 30%;
          animation-delay: 5s;
          animation-duration: 22s;
        }
        .bat:nth-child(3) {
          top: 60%;
          animation-delay: 10s;
          animation-duration: 20s;
        }

        @keyframes fly {
          from {
            left: -50px;
            transform: translateY(0) scaleX(1);
          }
          25% {
            transform: translateY(-30px) scaleX(-1);
          }
          50% {
            transform: translateY(20px) scaleX(1);
          }
          75% {
            transform: translateY(-40px) scaleX(-1);
          }
          to {
            left: 105%;
            transform: translateY(0) scaleX(1);
          }
        }

        .apex-nights-container {
          max-width: 1600px;
          margin: 0 auto;
          padding: 2rem;
          position: relative;
          z-index: 10;
        }

        .apex-nights-header {
          text-align: center;
          animation: fadeInDown 1s ease-out;
          display: block;
          margin-bottom: 3rem;
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .title {
          font-family: "Nosifer", cursive;
          font-size: 4.5rem;
          color: var(--blood-orange);
          text-shadow: 0 0 30px var(--blood-orange), 0 0 60px var(--toxic-green),
            4px 4px 0 var(--shadow-black);
          margin-bottom: 1rem;
          letter-spacing: 8px;
          animation: flicker 3s infinite;
        }

        @keyframes flicker {
          0%,
          100% {
            opacity: 1;
          }
          41%,
          43% {
            opacity: 0.9;
          }
          45% {
            opacity: 0.95;
          }
        }

        .subtitle {
          font-family: "Creepster", cursive;
          font-size: 2.2rem;
          color: var(--toxic-green);
          text-shadow: 2px 2px 0 var(--shadow-black);
          margin-bottom: 1rem;
          letter-spacing: 4px;
        }

        .tagline {
          font-size: 1.3rem;
          color: var(--moonlight);
          font-style: italic;
          max-width: 600px;
          margin: 0 auto;
        }

        /* Tab Navigation */
        .realm-navigator {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin: 3rem 0 2rem;
          position: relative;
          z-index: 10;
        }

        .realm-tab {
          font-family: "Eater", cursive;
          font-size: 1.4rem;
          padding: 1rem 2.5rem;
          background: linear-gradient(
            145deg,
            rgba(10, 10, 10, 0.8),
            rgba(45, 27, 105, 0.6)
          );
          border: 2px solid var(--rust);
          border-radius: 15px;
          color: var(--moonlight);
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          letter-spacing: 2px;
          text-shadow: 2px 2px 0 var(--shadow-black);
        }

        .realm-tab::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 69, 0, 0.3),
            transparent
          );
          transition: left 0.5s ease;
        }

        .realm-tab:hover::before {
          left: 100%;
        }

        .realm-tab:hover {
          border-color: var(--blood-orange);
          transform: translateY(-3px);
          box-shadow: 0 0 25px rgba(255, 69, 0, 0.4);
        }

        .realm-tab.active {
          background: linear-gradient(
            145deg,
            rgba(183, 65, 14, 0.4),
            rgba(45, 27, 105, 0.8)
          );
          border-color: var(--toxic-green);
          color: var(--toxic-green);
          box-shadow: 0 0 30px rgba(57, 255, 20, 0.4),
            inset 0 0 20px rgba(57, 255, 20, 0.2);
          text-shadow: 0 0 15px var(--toxic-green),
            2px 2px 0 var(--shadow-black);
        }

        .realm-tab-icon {
          margin-right: 0.5rem;
          font-size: 1.6rem;
          display: inline-block;
          animation: iconBounce 2s ease-in-out infinite;
        }

        @keyframes iconBounce {
          0%,
          100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-5px) rotate(5deg);
          }
        }

        /* Content Layout */
        .realm-content {
          animation: fadeIn 0.6s ease-out;
          position: relative;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .challenges-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
          margin-top: 2rem;
        }

        .challenge-card {
          background: linear-gradient(
            145deg,
            rgba(45, 27, 105, 0.7),
            rgba(10, 10, 10, 0.9)
          );
          border: 2px solid var(--rust);
          border-radius: 15px;
          padding: 2rem;
          position: relative;
          transition: all 0.3s ease;
          cursor: pointer;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(255, 69, 0, 0.2);
          text-decoration: none;
          color: inherit;
          display: flex;
          flex-direction: column;
          min-height: 400px;
          animation: cardRise 0.6s ease-out backwards;
        }

        @keyframes cardRise {
          from {
            opacity: 0;
            transform: translateY(40px) rotateX(20deg);
          }
          to {
            opacity: 1;
            transform: translateY(0) rotateX(0);
          }
        }

        .challenge-card:nth-child(1) {
          animation-delay: 0.1s;
        }
        .challenge-card:nth-child(2) {
          animation-delay: 0.2s;
        }
        .challenge-card:nth-child(3) {
          animation-delay: 0.3s;
        }
        .challenge-card:nth-child(4) {
          animation-delay: 0.4s;
        }
        .challenge-card:nth-child(5) {
          animation-delay: 0.5s;
        }
        .challenge-card:nth-child(6) {
          animation-delay: 0.6s;
        }
        .challenge-card:nth-child(7) {
          animation-delay: 0.7s;
        }

        .challenge-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(
            circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
            rgba(255, 69, 0, 0.15),
            transparent 60%
          );
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        .challenge-card:hover::before {
          opacity: 1;
        }

        .challenge-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 12px 48px rgba(255, 69, 0, 0.4);
          border-color: var(--blood-orange);
        }

        .challenge-card::after {
          content: "";
          position: absolute;
          top: -2px;
          left: -2px;
          right: -2px;
          bottom: -2px;
          background: linear-gradient(
            45deg,
            var(--blood-orange),
            var(--toxic-green),
            var(--blood-orange)
          );
          border-radius: 15px;
          z-index: -1;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .challenge-card:hover::after {
          opacity: 0.3;
          animation: borderGlow 2s linear infinite;
        }

        @keyframes borderGlow {
          0% {
            background-position: 0% 50%;
          }
          100% {
            background-position: 200% 50%;
          }
        }

        /* Placeholder cards */
        .placeholder-card {
          cursor: not-allowed;
          opacity: 0.6;
          // filter: blur(2px);
        }

        .placeholder-card:hover {
          transform: scale(0.98);
          box-shadow: 0 4px 16px rgba(192, 197, 206, 0.2);
        }

        .placeholder-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            rgba(10, 10, 10, 0.95),
            rgba(45, 27, 105, 0.9)
          );
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border-radius: 15px;
          backdrop-filter: blur(1px);
        }

        .placeholder-icon {
          font-size: 4rem;
          margin-bottom: 1rem;
          //  animation: crystalBall 5s ease-in-out infinite;
        }

        @keyframes crystalBall {
          0%,
          100% {
            transform: scale(1) rotate(0deg);
            filter: hue-rotate(0deg);
          }
          50% {
            transform: scale(1.1) rotate(180deg);
            filter: hue-rotate(60deg);
          }
        }

        .placeholder-message {
          font-family: "Creepster", cursive;
          font-size: 1.5rem;
          color: var(--moonlight);
          text-shadow: 2px 2px 0 var(--shadow-black);
          margin-bottom: 0.5rem;
        }

        .placeholder-submessage {
          font-size: 1rem;
          color: var(--moonlight);
          opacity: 0.7;
          font-style: italic;
        }

        /* Disabled card (not logged in) */
        .disabled-card {
          cursor: not-allowed;
          position: relative;
        }

        .login-tooltip {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(10, 10, 10, 0.95);
          border: 2px solid var(--blood-orange);
          border-radius: 12px;
          padding: 1.5rem 2rem;
          font-family: "Creepster", cursive;
          font-size: 1.2rem;
          color: var(--toxic-green);
          text-shadow: 2px 2px 0 var(--shadow-black);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
          z-index: 100;
          white-space: nowrap;
          box-shadow: 0 0 30px rgba(255, 69, 0, 0.5);
        }

        .disabled-card:hover .login-tooltip {
          opacity: 1;
        }

        .challenge-number {
          font-family: "Nosifer", cursive;
          font-size: 3rem;
          color: var(--blood-orange);
          text-shadow: 0 0 20px var(--blood-orange),
            2px 2px 0 var(--shadow-black);
          position: absolute;
          top: -10px;
          right: 20px;
          z-index: 1;
        }

        .placeholder-card .challenge-number {
          color: var(--moonlight);
          opacity: 0.4;
          text-shadow: 0 0 10px var(--midnight-purple),
            2px 2px 0 var(--shadow-black);
        }

        .challenge-title {
          font-family: "Creepster", cursive;
          font-size: 1.8rem;
          color: var(--toxic-green);
          margin-bottom: 1rem;
          text-shadow: 2px 2px 0 var(--shadow-black);
          letter-spacing: 2px;
        }

        .placeholder-card .challenge-title {
          color: var(--moonlight);
          opacity: 0.25;
          filter: blur(1px);
        }

        .challenge-level {
          display: inline-block;
          padding: 0.3rem 0.8rem;
          border-radius: 20px;
          font-size: 0.9rem;
          margin-bottom: 1rem;
          font-family: "Fira Code", monospace;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .level-10 {
          background: linear-gradient(135deg, #4caf50, #8bc34a);
          color: var(--shadow-black);
        }

        .level-20 {
          background: linear-gradient(135deg, #ff9800, #ffc107);
          color: var(--shadow-black);
        }

        .level-30 {
          background: linear-gradient(135deg, #f44336, #e91e63);
          color: var(--bone-white);
        }

        .challenge-description {
          color: var(--moonlight);
          line-height: 1.6;
          margin-bottom: 1rem;
          font-size: 1.1rem;
          flex-grow: 1;
        }

        .placeholder-card .challenge-description {
          opacity: 0.6;
          font-style: italic;
        }

        .code-preview {
          background: rgba(10, 10, 10, 0.8);
          border: 1px solid var(--toxic-green);
          border-radius: 8px;
          padding: 1rem;
          font-family: "Fira Code", monospace;
          font-size: 0.9rem;
          color: var(--toxic-green);
          overflow-x: auto;
          position: relative;
          margin-top: auto;
          white-space: pre-wrap;
          min-height: 80px;
        }

        .placeholder-card .code-preview {
          border: 1px solid var(--moonlight);
          opacity: 0.3;
        }

        .web {
          position: fixed;
          top: 0;
          right: 0;
          width: 300px;
          height: 300px;
          opacity: 0.3;
          pointer-events: none;
          z-index: 1;
        }

        .web svg {
          width: 100%;
          height: 100%;
        }

        .floating-particle {
          position: absolute;
          font-size: 1.5rem;
          pointer-events: none;
          animation: floatUp 2s ease-out forwards;
          z-index: 100;
        }

        @keyframes floatUp {
          to {
            transform: translateY(-200px) rotate(360deg);
            opacity: 0;
          }
        }

        /* Leaderboard container adjustments */
        .leaderboard-wrapper {
          margin-top: 2rem;
        }

        @media (max-width: 1024px) {
          .realm-navigator {
            flex-direction: column;
            align-items: center;
          }

          .realm-tab {
            width: 100%;
            max-width: 300px;
          }
        }

        @media (max-width: 768px) {
          .challenges-grid {
            grid-template-columns: 1fr;
          }

          .title {
            font-size: 2.5rem;
            letter-spacing: 4px;
          }

          .subtitle {
            font-size: 1.5rem;
          }

          .realm-tab {
            font-size: 1.2rem;
            padding: 0.8rem 1.5rem;
          }

          .challenge-card {
            min-height: 350px;
          }
        }
      `}</style>

      <div className="apex-after-dark">
        {/* Spider web decoration */}
        <div className="web">
          <svg viewBox="0 0 300 300">
            <g stroke="#C0C5CE" strokeWidth="1" fill="none" opacity="0.5">
              <path d="M150,0 L150,300 M0,150 L300,150 M0,0 L300,300 M300,0 L0,300" />
              <circle cx="150" cy="150" r="50" />
              <circle cx="150" cy="150" r="100" />
              <circle cx="150" cy="150" r="150" />
            </g>
          </svg>
        </div>

        <div className="apex-nights-container">
          <div className="apex-nights-header text-center">
            <h1 className="title">APEX WEEK OF HORROR</h1>
            <h2 className="subtitle">7 Nights of Code</h2>
            <p className="tagline">
              Practice Salesforce development through spine-chilling challenges
              that haunt your soul
            </p>
          </div>

          {/* Realm Navigator - Tab Navigation */}
          <div className="realm-navigator">
            <button
              className={`realm-tab ${
                activeTab === "challenges" ? "active" : ""
              }`}
              onClick={() => setActiveTab("challenges")}
            >
              <span className="realm-tab-icon">🕷️</span>
              The Trials
            </button>
            <button
              className={`realm-tab ${
                activeTab === "leaderboard" ? "active" : ""
              }`}
              onClick={() => setActiveTab("leaderboard")}
            >
              <span className="realm-tab-icon">🧙🏻‍♀️</span>
              Coven of Champions
            </button>
          </div>

          {/* Content Area */}
          <div className="realm-content">
            {activeTab === "challenges" && (
              <div className="challenges-grid">
                {filledChallenges.map((challenge, index) => {
                  const isPlaceholder = challenge.hide === true;
                  const cardClasses = `challenge-card ${
                    isPlaceholder ? "placeholder-card" : ""
                  } ${!isLoggedIn && !isPlaceholder ? "disabled-card" : ""}`;

                  // Wrapper component - either Link or div
                  const CardWrapper =
                    !isLoggedIn || isPlaceholder ? "div" : Link;
                  const cardProps =
                    !isLoggedIn || isPlaceholder
                      ? {}
                      : { to: `/advent/apex-week-of-horror/${challenge.slug}` };

                  return (
                    <CardWrapper
                      key={challenge._id || challenge.id}
                      {...cardProps}
                      className={cardClasses}
                      onMouseEnter={() =>
                        handleCardHover(
                          challenge.id || challenge._id,
                          isPlaceholder
                        )
                      }
                      style={{ position: "relative" }}
                      onClick={(e) => handleCardClick(e, challenge)}
                    >
                      {/* Login tooltip for non-logged-in users on real cards */}
                      {!isLoggedIn && !isPlaceholder && (
                        <div className="login-tooltip">
                          🔒 Sign in to unlock this challenge
                        </div>
                      )}

                      <span className="challenge-number">
                        {challenge.order}
                      </span>

                      {isPlaceholder ? (
                        <>
                          <h3 className="challenge-title">{challenge.name}</h3>
                          <p className="challenge-description">
                            {challenge.description}
                          </p>
                          <div className="code-preview">// Coming soon...</div>

                          <div className="placeholder-overlay">
                            <div className="placeholder-icon">
                              {challenge.unlockEmoji || "🔮"}
                            </div>
                            <div className="placeholder-message">
                              The ritual awaits...
                            </div>
                            <div className="placeholder-submessage">
                              Unlocks {challenge.unlockDate || "soon"}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <h3 className="challenge-title">{challenge.name}</h3>
                          <span
                            className={`challenge-level level-${challenge.points}`}
                          >
                            {challenge.points} points
                          </span>
                          <p className="challenge-description">
                            {challenge.description}
                          </p>
                          <div className="code-preview">
                            {challenge.codePreview || "// Start coding..."}
                          </div>
                        </>
                      )}

                      {/* Render particles for this card */}
                      {!isPlaceholder &&
                        particles
                          .filter(
                            (p) => p.cardId === (challenge.id || challenge._id)
                          )
                          .map((particle) => (
                            <div
                              key={particle.id}
                              className="floating-particle"
                              style={{
                                left: `${particle.left}%`,
                                bottom: 0,
                              }}
                            >
                              {particle.emoji}
                            </div>
                          ))}
                    </CardWrapper>
                  );
                })}
              </div>
            )}

            {activeTab === "leaderboard" && (
              <div className="leaderboard-wrapper">
                <AdventLeaderboard />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ApexAfterDark;
